import { useEffect, useState, useRef } from "react";
import { Grid, Typography, CircularProgress, Box } from "@mui/material";
import WhiteButton from "../Button/WhiteButton";
import BlueButton from "../Button/BlueButton";
import AxiosInstance from "../../utils/axiosInstance";
import InputText from "../InputFields/InputText";
import { useFormik } from "formik";
import Address from "../Address";
import "./style.css";
import { useUserType } from "../../contexts/UserTypeContext";
import { getTokenizationKey } from "../../plugins/ApiHandler";
import { useDejavooPayment } from "../../contexts/DejavooPaymentContext";
import { loadCardScript, cleanupPaymentMethods } from "../../plugins/helpers";
import { nmiCollectPosInlineStyleConfig } from "../../plugins/collectJsNmiPosShared";
import { formatCardNumberDisplay, formatExpiryDisplay } from "../../utils/gatewayHelper";

const CONTACT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddCardForm = (props) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address1: "",
    city: "",
    country: "",
    state: "",
    zip: "",
  });

  const [data, setData] = useState();
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [scriptError, setScriptError] = useState("");
  const [isPublicKeyAvailable, setIsPublicKeyAvailable] = useState(false);
  const [tokenizationKey, setTokenizationKey] = useState(null);
  const { userType } = useUserType();
  const { dejavoo } = useDejavooPayment();
  const isDonationMode = userType === "donor";
  const isDejavooGateway = (props?.Gateway || "").toString().toUpperCase() === "DEJAVOO";
  
  // Card field validation state
  const [cardFieldsValid, setCardFieldsValid] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });
  const [cardFieldErrors, setCardFieldErrors] = useState({
    ccnumber: "",
    ccexp: "",
    cvv: "",
  });
  const [cardFieldsTouched, setCardFieldsTouched] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });

  // Dejavoo card fields state
  const [dejavooCardData, setDejavooCardData] = useState({
    ccnumber: "",
    ccexpiry: "",
    cccvv: "",
  });

  const [contactTouched, setContactTouched] = useState({
    first_name: false,
    last_name: false,
    phone: false,
    email: false,
  });

  const getData = async () => {
    if (!props?.CustomerId || (props.CustomerId + "").trim() === "" || (props.CustomerId + "").trim() === "Company") {
      setData(null);
      return;
    }
    try {
      const res = await AxiosInstance.get(
        `/v1/customer/detail/${props?.CustomerId}`
      );
      setData(res?.data?.data);
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setData(null);
    }
  };

  useEffect(() => {
    getData();
  }, [props?.CustomerId]);

  const addressFormik = useFormik({
    initialValues: {
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Country: "",
    },
    onSubmit: () => {},
  });

  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d]/g, "");
    if (phoneNumber?.length < 1) {
      return phoneNumber;
    }
    if (phoneNumber?.length <= 3) {
      return `(${phoneNumber}`;
    }
    if (phoneNumber?.length <= 6) {
      return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3)}`;
    }
    return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(
      3,
      6
    )} - ${phoneNumber?.slice(6, 10)}`;
  };

  const handleChange = (event) => {
    const { name, value } = event?.target;

    if (name === "phone") {
      setFormData({
        ...formData,
        phone: formatPhoneNumber(value),
      });
    } else if (name === "ccnumber" || name === "ccexpiry" || name === "cccvv") {
      // Handle Dejavoo card fields
      let processedValue = value;
      
      // Format expiry to only allow digits and limit to 4 characters
      if (name === "ccexpiry") {
        processedValue = value.replace(/\D/g, "").slice(0, 4);
      }
      // Format CVV to only allow digits and limit to 4 characters
      else if (name === "cccvv") {
        processedValue = value.replace(/\D/g, "").slice(0, 4);
      }
      // Format card number to only allow digits and spaces
      else if (name === "ccnumber") {
        processedValue = value.replace(/[^\d\s]/g, "");
      }
      
      setDejavooCardData({
        ...dejavooCardData,
        [name]: processedValue,
      });
      
      // Validate Dejavoo card fields
      validateDejavooField(name, processedValue);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateDejavooField = (name, value) => {
    let isValid = false;
    let errorMessage = "";

    if (name === "ccnumber") {
      const cardNumber = value.replace(/\s/g, "");
      isValid = /^\d{13,19}$/.test(cardNumber);
      if (!value) {
        errorMessage = "Credit card number is required";
      } else if (!isValid) {
        errorMessage = "Card number must be 13-19 digits";
      }
    } else if (name === "ccexpiry") {
      const expiry = value.replace(/\s|\//g, "");
      if (!value) {
        errorMessage = "Expiration date is required";
      } else if (!/^\d{4}$/.test(expiry)) {
        errorMessage = "Invalid expiration date format. Use MMYY";
      } else {
        const month = parseInt(expiry.slice(0, 2), 10);
        const year = parseInt(expiry.slice(2, 4), 10);
        if (month < 1 || month > 12) {
          errorMessage = "Invalid month. Month must be between 01 and 12";
        } else {
          // Check if date is not in the past
          const currentYear = new Date().getFullYear() % 100;
          const currentMonth = new Date().getMonth() + 1;
          if (year < currentYear || (year === currentYear && month < currentMonth)) {
            errorMessage = "Expiration date cannot be in the past";
          } else {
            isValid = true;
          }
        }
      }
    } else if (name === "cccvv") {
      isValid = /^\d{3,4}$/.test(value);
      if (!value) {
        errorMessage = "CVV is required";
      } else if (!isValid) {
        errorMessage = "Invalid CVV. CVV must be 3-4 digits";
      }
    }

    setCardFieldsValid((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: isValid,
    }));

    setCardFieldErrors((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: errorMessage,
    }));

    setCardFieldsTouched((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: true,
    }));
  };

  const getNmiKey = async () => {
    // Skip NMI initialization if Dejavoo gateway
    if (isDejavooGateway) {
      setScriptGenerating(false);
      return;
    }
    
    setScriptGenerating(true);
    setScriptError("");

    try {
      cleanupPaymentMethods();

      const keyResponse = await getTokenizationKey(props?.CompanyId);
      if (keyResponse?.PublicKey) {
        setIsPublicKeyAvailable(true);
        setTokenizationKey(keyResponse.PublicKey);
        await loadCardScript(keyResponse.PublicKey);
      } else {
        setIsPublicKeyAvailable(false);
        setTokenizationKey(null);
      }
    } catch (error) {
      setScriptError(
        error?.message ||
          "Failed to load the tokenization script. Make sure you have suitable internet connection."
      );
    } finally {
      setScriptGenerating(false);
    }
  };

  /* global CollectJS */
  useEffect(() => {
    const configureCollectJS = () => {
      try {
        if (typeof CollectJS !== "undefined" && isPublicKeyAvailable && tokenizationKey) {
          CollectJS.configure({
            tokenizationKey: tokenizationKey,
            validationCallback: function (field, status, message) {
              // Track card field validation status
              setCardFieldsValid((prev) => ({
                ...prev,
                [field]: status,
              }));
              
              // Update error messages - use CollectJS message if available, otherwise use specific defaults
              let errorMessage = "";
              if (!status) {
                // Check if CollectJS provided a specific error message
                const messageLower = (message || "").toLowerCase();
                const isEmptyMessage = messageLower.includes("empty") || messageLower.includes("required") || messageLower === "field is empty";
                
                if (field === "ccnumber") {
                  // Replace generic "Field is empty" with specific message
                  if (isEmptyMessage) {
                    errorMessage = "Credit card number is required";
                  } else if (message && (messageLower.includes("invalid") || messageLower.includes("incorrect"))) {
                    errorMessage = "Invalid credit card number";
                  } else if (message && message.trim() !== "") {
                    errorMessage = message;
                  } else {
                    errorMessage = "Credit card number is required";
                  }
                } else if (field === "ccexp") {
                  // Replace generic "Field is empty" with specific message
                  if (isEmptyMessage) {
                    errorMessage = "Expiration date is required";
                  } else if (message && (messageLower.includes("expired") || messageLower.includes("past") || messageLower.includes("invalid date"))) {
                    errorMessage = "Expiration date cannot be in the past";
                  } else if (message && messageLower.includes("invalid")) {
                    errorMessage = "Invalid expiration date format. Use MM/YY";
                  } else if (message && message.trim() !== "") {
                    errorMessage = message;
                  } else {
                    errorMessage = "Expiration date is required";
                  }
                } else if (field === "cvv") {
                  // Replace generic "Field is empty" with specific message
                  if (isEmptyMessage) {
                    errorMessage = "CVV is required";
                  } else if (message && (messageLower.includes("invalid") || messageLower.includes("incorrect"))) {
                    errorMessage = "Invalid CVV. CVV must be 3-4 digits";
                  } else if (message && message.trim() !== "") {
                    errorMessage = message;
                  } else {
                    errorMessage = "CVV is required";
                  }
                } else {
                  // For other fields, replace "Field is empty" with "Field is required"
                  if (isEmptyMessage) {
                    errorMessage = "Field is required";
                  } else {
                    errorMessage = message || "Field is required";
                  }
                }
              }
              
              setCardFieldErrors((prev) => ({
                ...prev,
                [field]: errorMessage,
              }));
              
              // Mark field as touched when validation occurs
              setCardFieldsTouched((prev) => ({
                ...prev,
                [field]: true,
              }));
            },
            callback: async function (response) {
              if (response?.token) {
                // Collect.js may return masked card (e.g. 411111******1111) for storage in customer-vaults
                const displayCcNumber = (response?.card?.number || "").toString().trim();
                const last4 = displayCcNumber ? displayCcNumber.replace(/\D/g, "").slice(-4) : "";
                const responseData = {
                  ...formDataRef?.current,
                  payment_token: response?.token,
                  ...(displayCcNumber ? { displayCcNumber } : {}),
                  ...(last4 ? { last4 } : {}),
                  cardType: response?.card?.type || "",
                };
                props.addCardInfo(responseData);
              } else if (response?.error) {
                // Handle tokenization error - check for expiration date errors
                const errorLower = (response.error || "").toLowerCase();
                let errorMessage = response.error || "Card tokenization failed. Please check your card details.";
                
                // Check if error is related to expiration date
                if (errorLower.includes("expired") || errorLower.includes("expiration") || errorLower.includes("past")) {
                  setCardFieldErrors((prev) => ({
                    ...prev,
                    ccexp: "Expiration date cannot be in the past",
                    cardError: "",
                  }));
                  setCardFieldsValid((prev) => ({
                    ...prev,
                    ccexp: false,
                  }));
                  setCardFieldsTouched((prev) => ({
                    ...prev,
                    ccexp: true,
                  }));
                } else {
                  // Handle other tokenization errors
                  setCardFieldErrors((prev) => ({
                    ...prev,
                    cardError: errorMessage,
                  }));
                }
              }
            },
            ...nmiCollectPosInlineStyleConfig,
            fields: {
              ccnumber: { placeholder: "Credit Card (13-19 digits)" },
              ccexp: { placeholder: "MM/YY" },
              cvv: { placeholder: "CVV" },
            },
          });
        } else if (scriptGenerating) {
          setTimeout(() => {
            if (typeof CollectJS !== "undefined" && isPublicKeyAvailable && tokenizationKey) {
              configureCollectJS();
            }
          }, 100);
        } else if (!isPublicKeyAvailable) {
          console.warn("Public key not available for CollectJS configuration.");
        } else if (!tokenizationKey) {
          console.warn("Tokenization key not available for CollectJS configuration.");
        } else {
          console.warn("CollectJS is not defined.");
        }
      } catch (error) {
        console.error("An error occurred while configuring CollectJS:", error);
      }
    };

    if (!scriptGenerating && isPublicKeyAvailable && tokenizationKey) {
      configureCollectJS();
    }
  }, [scriptGenerating, isPublicKeyAvailable, tokenizationKey]);

  useEffect(() => {
    if (isDejavooGateway) {
      setScriptGenerating(false);
      setScriptError("");
    } else {
      getNmiKey();
    }
  }, [isDejavooGateway]);

  // Initialize Dejavoo script for frontend token generation (token from front, not backend)
  useEffect(() => {
    if (isDejavooGateway && props?.CompanyId && dejavoo?.status === "uninitialized") {
      dejavoo.initialize(props.CompanyId);
    }
  }, [isDejavooGateway, props?.CompanyId, dejavoo?.status]);

  useEffect(() => {
    return () => {
      cleanupPaymentMethods();
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const fd = formDataRef.current;
    const fnOk = (fd?.first_name || "").trim().length > 0;
    const lnOk = (fd?.last_name || "").trim().length > 0;
    const phoneD = (fd?.phone || "").replace(/\D/g, "");
    const em = (fd?.email || "").trim();
    const emailOk = em.length > 0 && CONTACT_EMAIL_RE.test(em);
    const phoneOk = phoneD.length >= 10;
    if (!fnOk || !lnOk || !phoneOk || !emailOk) {
      setContactTouched({
        first_name: true,
        last_name: true,
        phone: true,
        email: true,
      });
      return;
    }

    // Handle Dejavoo gateway
    if (isDejavooGateway) {
      // Validate Dejavoo card fields
      const dejavooFieldsValid = 
        cardFieldsValid.ccnumber && 
        cardFieldsValid.ccexp && 
        cardFieldsValid.cvv;
      
      if (!dejavooFieldsValid) {
        // Mark all card fields as touched to show errors
        setCardFieldsTouched({
          ccnumber: true,
          ccexp: true,
          cvv: true,
        });
        
        // Set error messages for empty/invalid fields
        const errors = {};
        if (!cardFieldsValid.ccnumber) {
          errors.ccnumber = dejavooCardData.ccnumber ? "Card number must be 13-19 digits" : "Credit card number is required";
        }
        if (!cardFieldsValid.ccexp) {
          errors.ccexp = dejavooCardData.ccexpiry ? "Invalid expiration date format. Use MMYY" : "Expiration date is required";
        }
        if (!cardFieldsValid.cvv) {
          errors.cvv = dejavooCardData.cccvv ? "Invalid CVV. CVV must be 3-4 digits" : "CVV is required";
        }
        setCardFieldErrors((prev) => ({ ...prev, ...errors }));
        
        // Scroll to card fields section
        const cardSection = document.getElementById("payment-fields");
        if (cardSection) {
          cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        
        return;
      }
      
      // Generate Dejavoo token from frontend (script postData), then pass token to backend
      if (dejavoo?.status !== "loaded" || !dejavoo?.postData) {
        setCardFieldErrors((prev) => ({
          ...prev,
          cardError: dejavoo?.error || "Payment system is not ready. Please wait or refresh the page.",
        }));
        const cardSection = document.getElementById("payment-fields");
        if (cardSection) cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      try {
        const response = await dejavoo.postData();
        if (!response) throw new Error("No response received from payment system");
        dejavoo.handleTransactionResponse(response);
        const token =
          response?.payment_token_id || response?.token || response?.dccd5389;
        if (response.statusCode === "00" && token) {
          // Get masked card number (4111********1111) from paymentCardToken API; store only this in DB
          // Check all possible response paths for ccNumber
          let displayCcNumber = (response?.ccNumber ||
                                response?.cc_number ||
                                response?.data?.ccNumber ||
                                response?.data?.cc_number ||
                                response?.data?.maskedCardNumber ||
                                response?.card?.ccNumber ||
                                "").toString().trim();
          if (!displayCcNumber) {
            const last4 = dejavooCardData?.ccnumber?.replace(/\D/g, "")?.slice(-4) || "";
            displayCcNumber = last4 ? `************${last4}` : "";
          }
          if (!displayCcNumber) {
            setCardFieldErrors((prev) => ({
              ...prev,
              cardError: "Failed to retrieve card display number. Please try again.",
            }));
            return;
          }
          const last4 = displayCcNumber.replace(/\D/g, "").slice(-4) || dejavooCardData.ccnumber?.replace(/\D/g, "")?.slice(-4) || "";
          const responseData = {
            ...formDataRef?.current,
            Gateway: "DEJAVOO",
            CustomerId: props?.CustomerId,
            CompanyId: props?.CompanyId,
            payment_token: String(token),
            payment_token_id: String(token),
            hashccnumber: last4,
            displayCcNumber: displayCcNumber,
          };
          console.log("AddCardForm Dejavoo: Sending to backend - displayCcNumber:", displayCcNumber, "full responseData:", responseData);
          try {
            await props.addCardInfo(responseData);
          } catch (addCardErr) {
            let errMsg = "Card could not be added. Please check your card details (e.g. card number, expiry, CVV) and try again.";
            if (typeof addCardErr === "string" && addCardErr.trim()) {
              errMsg = addCardErr.trim();
            } else if (addCardErr && typeof addCardErr === "object") {
              const m = addCardErr?.response?.data?.message ?? addCardErr?.response?.data?.data?.error ?? addCardErr?.response?.data?.error ?? addCardErr?.message;
              if (m != null && String(m).trim()) errMsg = String(m).trim();
            }
            setCardFieldErrors((prev) => ({
              ...prev,
              cardError: errMsg,
            }));
            const cardSection = document.getElementById("payment-fields");
            if (cardSection) cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } else {
          setCardFieldErrors((prev) => ({
            ...prev,
            cardError: response?.message || "Token generation failed.",
          }));
          const cardSection = document.getElementById("payment-fields");
          if (cardSection) cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch (err) {
        const errMsg =
          typeof err === "string"
            ? err
            : (err?.response?.data?.data?.error ||
               err?.response?.data?.error ||
               err?.response?.data?.message ||
               err?.message ||
               "Failed to generate card token.");
        setCardFieldErrors((prev) => ({
          ...prev,
          cardError: errMsg,
        }));
        const cardSection = document.getElementById("payment-fields");
        if (cardSection) cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    
    // Handle NMI gateway
    // Validate card fields before submission
    const cardFieldsEmpty = !cardFieldsValid.ccnumber || !cardFieldsValid.ccexp || !cardFieldsValid.cvv;
    
    if (cardFieldsEmpty) {
      // Mark all card fields as touched to show errors
      setCardFieldsTouched({
        ccnumber: true,
        ccexp: true,
        cvv: true,
      });
      
      // Set error messages for empty fields
      const errors = {};
      if (!cardFieldsValid.ccnumber) {
        errors.ccnumber = "Credit card number is required";
      }
      if (!cardFieldsValid.ccexp) {
        errors.ccexp = "Expiration date is required";
      }
      if (!cardFieldsValid.cvv) {
        errors.cvv = "CVV is required";
      }
      setCardFieldErrors((prev) => ({ ...prev, ...errors }));
      
      // Scroll to card fields section
      const cardSection = document.getElementById("payment-fields");
      if (cardSection) {
        cardSection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      
      return;
    }
    
    // If card fields are valid, trigger CollectJS tokenization
    if (typeof CollectJS !== "undefined" && isPublicKeyAvailable && tokenizationKey) {
      try {
        CollectJS.startPaymentRequest();
      } catch (error) {
        console.error("Error starting payment request:", error);
        setCardFieldErrors((prev) => ({
          ...prev,
          cardError: "Please complete all card fields",
        }));
      }
    }
  };

  useEffect(() => {
    if (!data?.locationDetails?.[0]) return;
    const loc = data.locationDetails[0];
    setFormData({
      first_name: data?.FirstName || "FirstName not available",
      last_name: data?.LastName || "LastName not available",
      email: data?.EmailAddress || "EmailAddress not available",
      phone: data?.PhoneNumber || "PhoneNumber not available",
      address1: loc?.Address || "Address not available",
      city: loc?.City || "City not available",
      state: loc?.State || "State not available",
      zip: loc?.Zip || "Zip not available",
      country: loc?.Country || "",
    });
    addressFormik.setValues({
      Address: loc?.Address || "",
      City: loc?.City || "",
      State: loc?.State || "",
      Zip: loc?.Zip || "",
      Country: loc?.Country || "",
    });
    setContactTouched({
      first_name: false,
      last_name: false,
      phone: false,
      email: false,
    });
  }, [data]);

  const phoneDigits = (formData?.phone || "").replace(/\D/g, "");
  const emailTrim = (formData?.email || "").trim();
  const emailFormatOk =
    emailTrim.length > 0 && CONTACT_EMAIL_RE.test(emailTrim);
  const firstNameError =
    contactTouched.first_name &&
    (formData?.first_name || "").trim().length === 0;
  const lastNameError =
    contactTouched.last_name &&
    (formData?.last_name || "").trim().length === 0;
  const phoneError =
    contactTouched.phone &&
    (phoneDigits.length === 0 || phoneDigits.length < 10);
  const emailError =
    contactTouched.email && (emailTrim.length === 0 || !emailFormatOk);
  const userInfoComplete =
    (formData?.first_name || "").trim().length > 0 &&
    (formData?.last_name || "").trim().length > 0 &&
    emailFormatOk &&
    phoneDigits.length >= 10;

  const addrVals = addressFormik.values;
  const addressComplete =
    (addrVals.Address || "").trim().length > 0 &&
    (addrVals.City || "").trim().length > 0 &&
    String(addrVals.State ?? "").trim().length > 0 &&
    (addrVals.Zip || "").trim().length > 0 &&
    String(addrVals.Country ?? "").trim().length > 0;

  const gatewayTrim = (props?.Gateway || "").toString().trim();
  const dejavooCardFieldsOk =
    cardFieldsValid.ccnumber && cardFieldsValid.ccexp && cardFieldsValid.cvv;

  let paymentFieldsReady = false;
  if (!gatewayTrim) {
    paymentFieldsReady = false;
  } else if (isDejavooGateway) {
    paymentFieldsReady = dejavoo?.status === "loaded" && dejavooCardFieldsOk;
  } else {
    paymentFieldsReady =
      !scriptGenerating &&
      !scriptError &&
      isPublicKeyAvailable &&
      Boolean(tokenizationKey) &&
      dejavooCardFieldsOk;
  }

  const canSubmitAddCard =
    userInfoComplete && addressComplete && paymentFieldsReady;

  return (
    <form
      className="show-form"
      onSubmit={handleSubmit}
      style={{ margin: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
      onKeyDown={(e) => {
        if (
          e.key === "Enter" &&
          e.target.tagName !== "TEXTAREA" &&
          !e.target.classList.contains("MuiAutocomplete-input")
        ) {
          e.preventDefault();
          const payButton = document.getElementById("payButton");
          if (payButton && !payButton.disabled) {
            payButton.click();
          }
        }
      }}
    >
      <Box className="formInner" sx={{ width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}>
        <Grid container spacing={2} sx={{ width: "100%", mt: 0, maxWidth: "100%" }}>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <InputText
              onChange={handleChange}
              onBlur={() =>
                setContactTouched((prev) => ({ ...prev, first_name: true }))
              }
              name="first_name"
              required
              label="First Name"
              fullWidth
              value={formData?.first_name}
              error={firstNameError}
              showErrorOnly
              InputLabelProps={{
                shrink: Boolean(formData?.first_name),
              }}
              className="text-blue-color w-100"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <InputText
              onChange={handleChange}
              onBlur={() =>
                setContactTouched((prev) => ({ ...prev, last_name: true }))
              }
              required
              name="last_name"
              label="Last Name"
              fullWidth
              value={formData?.last_name}
              error={lastNameError}
              showErrorOnly
              InputLabelProps={{
                shrink: Boolean(formData?.last_name),
              }}
              className="text-blue-color w-100 userLarName"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <InputText
              onChange={handleChange}
              onBlur={() =>
                setContactTouched((prev) => ({ ...prev, phone: true }))
              }
              placeholder="Enter Phone Number"
              label="Phone Number"
              required
              name="phone"
              type="text"
              inputProps={{ inputMode: "tel", autoComplete: "tel" }}
              fullWidth
              value={formData?.phone}
              error={phoneError}
              showErrorOnly
              InputLabelProps={{
                shrink: Boolean(formData?.phone),
              }}
              className="text-blue-color w-100 userLarName"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <InputText
              onChange={handleChange}
              onBlur={() =>
                setContactTouched((prev) => ({ ...prev, email: true }))
              }
              required
              placeholder="Enter Email"
              label="Email"
              name="email"
              type="text"
              inputProps={{ autoComplete: "email" }}
              fullWidth
              value={formData?.email}
              error={emailError}
              showErrorOnly
              InputLabelProps={{
                shrink: Boolean(formData?.email),
              }}
              className="text-blue-color w-100 userLarName"
            />
          </Grid>
          <Grid size={12}>
            <Address
              formik={addressFormik}
              handleChange={(e) => {
                const { name, value } = e?.target || {};
                if (name) addressFormik.setFieldValue(name, value);
              }}
              onAddressDataForPayment={(payload) =>
                setFormData((prev) => ({
                  ...prev,
                  address1: payload.address1 ?? prev.address1,
                  city: payload.city ?? prev.city,
                  state: payload.state ?? prev.state,
                  zip: payload.zip ?? prev.zip,
                  country: payload.countryCode || prev.country,
                }))
              }
              submitAttempted={props?.submitAttempted}
            />
          </Grid>
          <Grid size={12}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
        {/* NMI: Collect.js tokenizes card and we pass masked number to customer-vaults. Dejavoo: Freedom to Design (FTD) or direct fields. */}

        {!props?.Gateway || (props?.Gateway || "").toString().trim() === "" ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              No payment gateway configured
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please configure a payment gateway to proceed.
            </Typography>
          </Box>
        ) : (scriptGenerating || (isDejavooGateway && (dejavoo?.status === "loading" || dejavoo?.status === "uninitialized"))) ? (
          <Grid className="d-flex justify-content-center align-items-center p-4" style={{ minHeight: "200px" }}>
            <CircularProgress 
              size={40} 
              style={{ color: "#0f2b46" }} 
              indeterminate
            />
            <Typography className="ms-3 text-blue-color" style={{ fontSize: "16px" }}>
              Loading payment fields...
            </Typography>
          </Grid>
        ) : scriptError ? (
          <Grid className="d-flex justify-content-center align-items-center p-4" style={{ minHeight: "200px" }}>
            <Typography className="text-center" style={{ color: "#d32f2f", fontSize: "14px" }}>
                {scriptError}
            </Typography>
          </Grid>
        ) : isDejavooGateway ? (
          <Grid id="payment-fields" sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
            {cardFieldErrors.cardError && (
              <Typography
                variant="body2"
                sx={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  marginBottom: "8px",
                  marginLeft: "14px",
                  fontWeight: 500,
                }}
              >
                {cardFieldErrors.cardError}
              </Typography>
            )}
            {/* FTD freedomtodesign.js postData() reads #ccnumber, #ccexpiry, #cccvv — keep ids aligned with name (no custom id). */}
            <Box className="dejavoo-native-card-fields">
            <Grid container spacing={2} className="monthCvvCardInput" sx={{ width: "100%", maxWidth: "100%" }}>
              <Grid size={12}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label" htmlFor="ccnumber">
                    Credit Card
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                <InputText
                  name="ccnumber"
                  placeholder="13–19 digits"
                  value={formatCardNumberDisplay(dejavooCardData.ccnumber)}
                  onChange={handleChange}
                  error={cardFieldsTouched.ccnumber && !!cardFieldErrors.ccnumber}
                  fullWidth
                  fieldHeight="44px"
                  className="text-blue-color w-100"
                  InputLabelProps={{
                    shrink: Boolean(dejavooCardData.ccnumber),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root fieldset": {
                      borderColor: cardFieldsValid.ccnumber && dejavooCardData.ccnumber ? "#14855F !important" : (cardFieldErrors.ccnumber ? "#B40E3E !important" : undefined),
                      borderTopColor: cardFieldsValid.ccnumber && dejavooCardData.ccnumber ? "#14855F !important" : (cardFieldErrors.ccnumber ? "#B40E3E !important" : undefined),
                    },
                    "& .MuiInputBase-input": {
                      color: cardFieldsValid.ccnumber && dejavooCardData.ccnumber ? "#14855F !important" : (cardFieldErrors.ccnumber ? "#B40E3E !important" : undefined),
                    },
                  }}
                />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label" htmlFor="ccexpiry">
                    MM / YY
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                <InputText
                  name="ccexpiry"
                  placeholder="MM/YY"
                  value={formatExpiryDisplay(dejavooCardData.ccexpiry)}
                  onChange={handleChange}
                  error={cardFieldsTouched.ccexp && !!cardFieldErrors.ccexp}
                  fullWidth
                  fieldHeight="44px"
                  className="text-blue-color w-100"
                  InputLabelProps={{
                    shrink: Boolean(dejavooCardData.ccexpiry),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root fieldset": {
                      borderColor: cardFieldsValid.ccexp && dejavooCardData.ccexpiry ? "#14855F !important" : (cardFieldErrors.ccexp ? "#B40E3E !important" : undefined),
                      borderTopColor: cardFieldsValid.ccexp && dejavooCardData.ccexpiry ? "#14855F !important" : (cardFieldErrors.ccexp ? "#B40E3E !important" : undefined),
                    },
                    "& .MuiInputBase-input": {
                      color: cardFieldsValid.ccexp && dejavooCardData.ccexpiry ? "#14855F !important" : (cardFieldErrors.ccexp ? "#B40E3E !important" : undefined),
                    },
                  }}
                />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label" htmlFor="cccvv">
                    CVV
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                <InputText
                  name="cccvv"
                  placeholder="3–4 digits"
                  value={dejavooCardData.cccvv}
                  onChange={handleChange}
                  error={cardFieldsTouched.cvv && !!cardFieldErrors.cvv}
                  fullWidth
                  fieldHeight="44px"
                  className="text-blue-color w-100"
                  InputLabelProps={{
                    shrink: Boolean(dejavooCardData.cccvv),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root fieldset": {
                      borderColor: cardFieldsValid.cvv && dejavooCardData.cccvv ? "#14855F !important" : (cardFieldErrors.cvv ? "#B40E3E !important" : undefined),
                      borderTopColor: cardFieldsValid.cvv && dejavooCardData.cccvv ? "#14855F !important" : (cardFieldErrors.cvv ? "#B40E3E !important" : undefined),
                    },
                    "& .MuiInputBase-input": {
                      color: cardFieldsValid.cvv && dejavooCardData.cccvv ? "#14855F !important" : (cardFieldErrors.cvv ? "#B40E3E !important" : undefined),
                    },
                  }}
                />
                </Box>
              </Grid>
            </Grid>
            </Box>
          </Grid>
        ) : (
          <Grid id="payment-fields">
            <Grid container spacing={2} className="monthCvvCardInput">
              <Grid size={12}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label">
                    Credit Card
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                  <Grid className="payment-field" id="ccnumber" />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label">
                    MM / YY
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                  <Grid className="payment-field monthCardDetailInput" id="ccexp" />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label className="cs-rs__label">
                    CVV
                    <span className="cs-field-label-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  </label>
                  <Grid className="payment-field monthCardDetailInput" id="cvv" />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
        </Box>
          </Grid>
          <Grid
            className="flex-bottom BlueAndWhiteBtmFlex"
            sx={{
              pt: 2,
              mt: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              width: "100%",
            }}
            size={12}>
            <WhiteButton
              type="button"
              label="Cancel"
              style={{ minHeight: 42 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props?.onHide();
              }}
            />
            <BlueButton
              type="submit"
              id="payButton"
              isDonationMode={isDonationMode}
              style={{ minHeight: 42 }}
              disabled={
                props?.loader ||
                scriptGenerating ||
                !canSubmitAddCard
              }
              label={
                props?.loader ? (
                  <CircularProgress size={20} style={{ color: "#fff" }} />
                ) : (
                  "Add Card"
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

export default AddCardForm;
